import React, { useCallback, useMemo, useRef, useState } from "react";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import Form, { SimpleItem as Item, GroupItem } from "devextreme-react/form";
import { apiBaseURL } from "../../../Utils/ProjectConstants";
import useFetchSupervisors from "../../../Hooks/dataFetcher/useFetchSupervisors";
import handlePostWorkPlan from "../../../Utils/handlePOST/handlePostWorkPlan";
import handlePutWorkPlan from "../../../Utils/handlePUT/handlePutWorkPlan";
import useFetch from "../../../Hooks/useFetch";
import { useNavigate } from "react-router-dom";

export default function TaskFormPopup({
  onClose,
  data,
  setNotification,
  mutate,
}) {
  const navigate = useNavigate(),
    fetch = useFetch(navigate);
  const isNewForm = !!data?.newForm;

  // Safely unwrap row data for EDIT mode (handles rowData.data or rowData)
  const rd = (data?.rowData?.data ?? data?.rowData ?? {}) || {};

  const initialFormData = isNewForm
    ? {
        taskName: null,
        startDate: null,
        endDate: null,
        supervisorIds: [], // TagBox prefers an array
        completedPercentage: null,
        allocatedEngHours: null,
        description: "",
        exitCriteria: "",
        remarks: null,
      }
    : {
        taskName: rd.taskName ?? "",
        startDate: rd.startDate ?? null,
        endDate: rd.endDate ?? null,
        supervisorIds: rd.supervisorIds ?? [],
        completedPercentage: rd.completedPercentage ?? null,
        allocatedEngHours: rd.allocatedEngHours ?? null,

        // Be lenient about server key casing/shapes
        description: rd.description ?? rd.Description ?? "",
        exitCriteria:
          rd.exitCriteria ??
          rd.ExitCriteria ??
          rd.exit_criteria ??
          rd.exitcriteria ??
          "",

        remarks: rd.remarks ?? rd.Remarks ?? "",
      };

  const { allSupervisors } = useFetchSupervisors();
  const formRef = useRef({}),
    [formData, setFormData] = useState(initialFormData);
  const [isLoading, setLoading] = useState(false);

  const cancelBtnOptions = useCallback(() => {
    return {
      text: "Cancel",
      onClick: () => onClose(),
    };
  }, [onClose]);

  const handleSave = useCallback(async () => {
    setLoading(true);
    const formValidation = formRef.current.instance.validate();

    if (formValidation.isValid) {
      let notification, checkData;

      if (isNewForm) {
        const projectId = data?.rowData?.data?.projectId || data.rowData,
          headId = data?.rowData?.key || "";
        const { response, isDataGood } = await handlePostWorkPlan({
          fetch,
          formData,
          projectId,
          headId,
        });
        notification = response;
        checkData = isDataGood;
      } else {
        const id = data?.rowData?.data?.id,
          billingCode = data.rowData.key,
          headId = data?.rowData?.node?.parent?.key || "";

        const { response, isDataGood } = await handlePutWorkPlan({
          fetch,
          formData,
          id,
          billingCode,
          headId,
        });
        notification = response;
        checkData = isDataGood;
      }

      setNotification(notification);
      if (checkData) {
        mutate();
        onClose();
      }
    }
    setLoading(false);
  }, [data, formData, fetch, setNotification, mutate, onClose, isNewForm]);

  const confirmBtnOptions = useCallback(() => {
    return {
      text: isNewForm ? "Save" : "Update",
      onClick: () => handleSave(),
    };
  }, [handleSave, isNewForm]);

  const supervisorOptions = useMemo(() => {
    return {
      dataSource: allSupervisors,
      valueExpr: "value",
      displayExpr: "label",
      selectionMode: "multiple",
      searchEnabled: true,
      placeholder: "Type or Select...",
    };
  }, [allSupervisors]);

  return (
    <Popup
      title={isNewForm ? "Add Task" : "Edit Task"}
      showTitle={true}
      visible={true}
      onHiding={onClose}
      width="50%"
      height="90vh"
      maxHeight="90vh"
      dragEnabled={true}
      resizeEnabled={true}
      position={{ of: window, my: "center", at: "center", collision: "fit" }}
    >
      <ToolbarItem
        widget="dxButton"
        location="after"
        toolbar="bottom"
        disabled={isLoading}
        options={confirmBtnOptions()}
      />
      <ToolbarItem
        widget="dxButton"
        location="after"
        toolbar="bottom"
        options={cancelBtnOptions()}
      />

      {/* Scrollable content */}
      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          scrollBehavior: "smooth",
          marginRight: "-8px", // pulls scrollbar flush to the edge
          paddingRight: "8px", // keeps text clear but tight
        }}
      >
        <Form formData={formData} ref={formRef} showValidationSummary={true}>
          <GroupItem colSpan={4}>
            <GroupItem colCount={4} colSpan={2}>
              <Item dataField="taskName" colSpan={4} isRequired={true} />
              <Item
                dataField="startDate"
                editorType="dxDateBox"
                colSpan={2}
                isRequired={true}
              />
              <Item
                dataField="endDate"
                editorType="dxDateBox"
                colSpan={2}
                isRequired={true}
              />
            </GroupItem>

            <Item
              dataField="supervisorIds"
              editorType="dxTagBox"
              editorOptions={supervisorOptions}
              colSpan={4}
              isRequired={true}
            />
            <Item
              dataField="completedPercentage"
              name="% Complete"
              colSpan={4}
              editorType="dxNumberBox"
              isRequired={true}
            />
            <Item
              dataField="allocatedEngHours"
              colSpan={4}
              editorType="dxNumberBox"
              isRequired={true}
            />

            <Item
              dataField="description"
              editorType="dxTextArea"
              editorOptions={{ height: 90 }}
              colSpan={4}
              isRequired={true}
            />
            <Item
              dataField="exitCriteria"
              editorType="dxTextArea"
              editorOptions={{ height: 90 }}
              colSpan={4}
              isRequired={true}
            />

            <Item
              dataField="remarks"
              editorType="dxTextArea"
              editorOptions={{ height: 90 }}
              visible={!isNewForm}
              isRequired={true}
            />
          </GroupItem>
        </Form>
      </div>
    </Popup>
  );
}
